import React from 'react';
import styled from 'styled-components';
import { Plain, Rich } from '@agendor/shared';
import { vars } from '@agendor/ui';

const Container = styled.div`
    display: inline-flex;
    align-items: stretch;
    height: 36px;
    background-color: #fff;
    border-radius: 6px;
    border: 1px solid ${vars.dividerColor};
    position: relative;
    overflow: hidden;
`;

const NavButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    color: ${vars.foregroundColor};
    padding: 0 12px;
    font-size: 1.25rem;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: ${vars.backgroundColorBright};
    }

    &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }
`;

const DisplayContainer = styled.div`
    position: relative;
    min-width: 160px;
    display: flex;
    justify-content: center;
    align-items: stretch;
    flex: 1;
    border-left: 1px solid ${vars.dividerColor};
    border-right: 1px solid ${vars.dividerColor};

    &:hover > div {
        background-color: ${vars.backgroundColorBright};
    }
`;

const Display = styled.div`
    font-size: 1rem;
    font-weight: 400;
    color: ${vars.foregroundColor};
    text-align: center;
    text-transform: capitalize;
    user-select: none;
    padding: 0 12px;
    width: 100%;
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
`;

const monthFormatter = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });

function isSameOrAfter(a: Plain.Month, b: Plain.Month): boolean {
    return a.year > b.year || (a.year === b.year && a.month >= b.month);
}

function isSameOrBefore(a: Plain.Month, b: Plain.Month): boolean {
    return a.year < b.year || (a.year === b.year && a.month <= b.month);
}

type Props = {
    month: Plain.Month;
    onChange: (month: Plain.Month) => void;
    minMonth?: Plain.Month;
    maxMonth?: Plain.Month;
    prevLabel?: string;
    nextLabel?: string;
    prevTestId?: string;
    nextTestId?: string;
    /**
     * Optional overlay rendered on top of the label (e.g. a transparent
     * `<input type="month">` for native month picking). Absolutely positioned
     * over the label container.
     */
    overlay?: React.ReactNode;
};

export default function MonthNavigation({
    month,
    onChange,
    minMonth,
    maxMonth,
    prevLabel = 'Mês anterior',
    nextLabel = 'Próximo mês',
    prevTestId,
    nextTestId,
    overlay,
}: Props) {
    const rich = new Rich.Month(month);
    const label = monthFormatter.format(new Date(month.year, month.month - 1, 1));
    const prevDisabled = minMonth ? isSameOrBefore(month, minMonth) : false;
    const nextDisabled = maxMonth ? isSameOrAfter(month, maxMonth) : false;
    return (
        <Container>
            <NavButton
                onClick={() => onChange(rich.adding({ months: -1 }).plain)}
                disabled={prevDisabled}
                aria-label={prevLabel}
                data-testid={prevTestId}
            >
                ‹
            </NavButton>
            <DisplayContainer>
                <Display>{label}</Display>
                {overlay}
            </DisplayContainer>
            <NavButton
                onClick={() => onChange(rich.adding({ months: 1 }).plain)}
                disabled={nextDisabled}
                aria-label={nextLabel}
                data-testid={nextTestId}
            >
                ›
            </NavButton>
        </Container>
    );
}
